import {useCallback, useMemo} from 'react';
import {useDispatch} from 'react-redux';
import {useParams} from 'react-router-dom';

import {useMemoizedSelector} from '../../../../../state/hooks';
import * as PanelActions from '../../../../../state/views/panel/actions';
import {type Ref as SectionRef} from '../../../../../state/views/panelBankSectionConfig/types';
import {useRampFlagVideoStreaming} from '../../../../../util/rampFeatureFlags/rampFeatureFlags';
import {type RampKey} from '../../../../../util/useRampFlag';
import {type MediaBrowserPanelConfig} from '../../../../PanelMediaBrowser';
import {
  type MediaPanelConfigWithRef,
  selectMediaPanelConfigsInSection,
} from '../mediaSettingsSelectors';

export function getNumberOfOverridesInMediaPanels(
  mediaPanelConfigsWithRefs: MediaPanelConfigWithRef[]
) {
  return mediaPanelConfigsWithRefs.reduce(
    (acc, {config}) => {
      Object.keys(config).forEach((key: string) => {
        const configKey = key as keyof MediaBrowserPanelConfig; // we already checked the panel type, and we know this is coming from a key of the config.
        // `null` is the explicit-clear sentinel and counts as an override.
        if (config[configKey] !== undefined) {
          acc[configKey] = (acc[configKey] ?? 0) + 1;
        }
      });
      return acc;
    },
    {} as Partial<Record<keyof MediaBrowserPanelConfig, number>>
  );
}

/**
 * Counts section-level media setting overrides.
 * Only counts overrides for keys that exist in the workspace config (i.e., settings
 * that actually have a workspace-level value that could be overridden).
 *
 * @param sectionsWithMediaSettings - Array of sections with their media settings
 * @param workspaceSettingsConfig - The workspace-level media settings config
 * @returns Object mapping config keys to their override counts
 */
export function getNumberOfOverridesInMediaSections(
  sectionsWithMediaSettings: Array<{
    sectionSettings: {media: MediaBrowserPanelConfig};
  }>,
  workspaceSettingsConfig: Partial<MediaBrowserPanelConfig>
): Partial<Record<keyof MediaBrowserPanelConfig, number>> {
  return sectionsWithMediaSettings.reduce(
    (acc, {sectionSettings}) => {
      Object.keys(sectionSettings.media).forEach(key => {
        const configKey = key as keyof MediaBrowserPanelConfig;
        // Only count if workspace has this setting (there's something to
        // override); a workspace `null` (explicit clear) still counts as set.
        if (workspaceSettingsConfig[configKey] !== undefined) {
          acc[configKey] = (acc[configKey] ?? 0) + 1;
        }
      });
      return acc;
    },
    {} as Partial<Record<keyof MediaBrowserPanelConfig, number>>
  );
}

export function unsetConfigFieldInMediaPanels(
  mediaPanelConfigsWithRefs: MediaPanelConfigWithRef[],
  field: keyof MediaBrowserPanelConfig
) {
  return mediaPanelConfigsWithRefs.map(({config}) => {
    const {[field]: _, ...newConfig} = config;
    return newConfig;
  });
}

export const useMediaSectionOverrides = (sectionRef: SectionRef) => {
  // Only grabs the media type panels in the section
  const mediaPanelConfigsInSection = useMemoizedSelector(
    selectMediaPanelConfigsInSection,
    sectionRef
  );
  const mediaPanelRefs = useMemo(
    () => mediaPanelConfigsInSection.map(({ref}) => ref),
    [mediaPanelConfigsInSection]
  );
  const numberOfOverridesBelow = useMemo(
    () => getNumberOfOverridesInMediaPanels(mediaPanelConfigsInSection),
    [mediaPanelConfigsInSection]
  );

  const dispatch = useDispatch();

  const clearOverridesBelow = useCallback(
    (field: keyof MediaBrowserPanelConfig) => {
      return dispatch(
        PanelActions.setConfigs(
          mediaPanelRefs,
          unsetConfigFieldInMediaPanels(mediaPanelConfigsInSection, field)
        )
      );
    },
    [mediaPanelRefs, dispatch, mediaPanelConfigsInSection]
  );
  return {numberOfOverridesBelow, clearOverridesBelow};
};

export function useRampFlagsForMediaSettings(): Partial<
  Record<RampKey, boolean>
> {
  const entityName = useParams<{entityName: string}>().entityName;
  const isVideoStreamingEnabled = useRampFlagVideoStreaming(entityName);
  return {
    video_streaming_entity: isVideoStreamingEnabled,
  };
}
